/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-param-reassign */
/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { uniq } from 'ramda'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Snackbar from '@mui/material/Snackbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import useSession from 'src/authentication/hooks/use-session'
import {
  fetchLearningPaths,
  selectLearningPaths,
  selectLearningPathsStatus,
  fetchLearningPathCourses,
  selectLearningPathCourses,
  selectLearningPathCoursesStatus,
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
  fetchUser,
  selectUser,
  selectUserStatus,
} from 'src/v3/profile/profile.slice'
import {
  CancelIcon,
  CorrectIcon,
  IncorrectIcon,
  LearningPathIcon,
  ResetIcon,
} from 'src/v3/core/icons'
import Subheader from 'src/v3/components/subheader'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'
import { LearningPath } from '../core/types'

export default (): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let session = useSession()

  let userId = session?.userId
  let learningPaths = useAppSelector(selectLearningPaths)
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let courses = useAppSelector(selectLearningPathCourses)
  let coursesStatus = useAppSelector(selectLearningPathCoursesStatus)
  let user = useAppSelector(selectUser)
  let userStatus = useAppSelector(selectUserStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let categories = uniq(learningPaths.map((lp) => lp.category)).filter(
    (category) => category.toLowerCase() !== 'new york',
  )

  let initCategories = () => {
    let storedSelectedCategories = localStorage.getItem('selectedCategories')

    if (storedSelectedCategories !== null) {
      return storedSelectedCategories.split(',').filter((c) => !!c)
    }

    let autoCategories = categories.filter(
      (c) =>
        user?.licenseType?.includes(c) &&
        !(user?.licenseType === 'AEMT' && c === 'EMT'),
    )

    return autoCategories.length ? autoCategories : categories
  }

  let [selected, setSelected] = useState<string>('')
  let [selectedCategories, setSelectedCategories] =
    useState<string[]>(initCategories())
  let [dialog, setDialog] = useState<string>('')
  let [successMessage, setSuccessMessage] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  let learningPathsByCreator = learningPaths.reduce(
    (dict, lp) => {
      if (
        selectedCategories.includes(lp.category) &&
        lp.category.toLowerCase() !== 'new york'
      ) {
        let lsName =
          linkedServices.find((ls) => ls.id === lp.linkedServiceID)?.name ||
          'FOAMfrat'
        dict[lsName] ??= {}
        dict[lsName][lp.category] ??= []
        dict[lsName][lp.category].push(lp)
      }
      return dict
    },
    {} as Record<string, Record<string, LearningPath[]>>,
  )

  let selectedPath = learningPaths.find((lp) => lp.id === selected)

  let pathCompletion =
    !selectedPath || !selected
      ? 0
      : (courses[selected]?.filter((c) => c.status === 'Complete').length /
          selectedPath.courseIDs.length) *
        100

  let handleCategoryToggle = (value: string) => {
    let currentIndex = selectedCategories.indexOf(value)
    let newCategories = [...selectedCategories].filter((c) => !!c)

    if (currentIndex === -1) {
      newCategories.push(value)
    } else {
      newCategories.splice(currentIndex, 1)
    }

    setSelectedCategories(newCategories)
    localStorage.setItem('selectedCategories', newCategories.join(','))
  }

  useEffect(() => {
    if (learningPathsStatus === 'uninitialized') {
      dispatch(fetchLearningPaths())
    } else if (learningPathsStatus === 'fulfilled') {
      setSelectedCategories(initCategories())
    }
  }, [learningPathsStatus])

  useEffect(() => {
    if (selected && (!courses[selected] || courses[selected].length === 0)) {
      setSelected('')
      setErrorMessage('No Course Information Found')
    }
  }, [selected])

  useEffect(() => {
    if (userStatus === 'uninitialized' && userId) {
      dispatch(fetchUser())
    }
  }, [userStatus, userId])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  return (
    <>
      <div className="flex items-center justify-center my-2">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <LearningPathIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Learning Paths
        </Typography>
      </div>
      <Typography
        textAlign="center"
        color="text.secondary"
        sx={{ marginBottom: -1 }}
      >
        View courses categorized and grouped in learning paths
      </Typography>
      <Box
        sx={{
          mt: 2,
          pl: 1,
          borderLeft: `10px solid ${theme.palette.background.paper}`,
        }}
      >
        <Typography sx={{ ml: 0.5 }} variant="h6">
          Filter Categories
        </Typography>
        <Typography
          sx={{ ml: 0.5, mb: 1, fontStyle: 'italic' }}
          color="text.secondary"
          variant="subtitle2"
        >
          Only learning paths belonging to the selected categories will be
          visible.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              color={
                selectedCategories.includes(category) ? 'primary' : 'default'
              }
              variant={
                selectedCategories.includes(category) ? 'filled' : 'outlined'
              }
              onClick={() => handleCategoryToggle(category)}
            />
          ))}
        </Box>
      </Box>
      {Object.entries(learningPathsByCreator).map(
        ([creator, learningPathsByCategory]) => {
          let linkedService = linkedServices.find((ls) => ls.name === creator)
          return (
            <React.Fragment key={creator}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: -2.25,
                  mt: 3,
                  pb: 0.5,
                  borderBottom: `1px solid #484848`,
                }}
              >
                <Avatar
                  sx={{ ml: 1.5 }}
                  src={
                    linkedService
                      ? `${import.meta.env.VITE_CLOUDFRONT_URL}/public/${linkedService?.imageName}`
                      : '/logo.svg'
                  }
                />
                <Typography variant="h5">{creator} Learning Paths</Typography>
              </Box>
              {Object.entries(learningPathsByCategory).map(
                ([category, categoryLearningPaths]) => (
                  <Box key={category}>
                    <Subheader title={category} />
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      {categoryLearningPaths.map((lp) => {
                        let completed = courses[lp.id]?.every(
                          (c) => c.status === 'Complete',
                        )
                        return (
                          <Box
                            key={lp.id}
                            sx={{
                              flex: '0 0 240px',
                              border: `1px solid ${theme.palette.primary.dark}`,
                              borderRadius: 2,
                              filter: completed
                                ? 'grayscale(0)'
                                : 'grayscale(0.8)',
                              '&:hover': {
                                cursor: 'pointer',
                                filter: 'grayscale(0)',
                              },
                            }}
                            onClick={() => {
                              if (
                                coursesStatus[lp.id] === 'uninitialized' ||
                                !coursesStatus[lp.id]
                              ) {
                                dispatch(fetchLearningPathCourses(lp.id))
                                  .then(() => setSelected(lp.id))
                                  .catch((err) =>
                                    setErrorMessage(
                                      err?.message || 'Error Finding Courses',
                                    ),
                                  )
                              } else {
                                setSelected(lp.id)
                              }
                            }}
                          >
                            {completed && (
                              <Box sx={{ height: 0 }}>
                                <CorrectIcon
                                  sx={{
                                    position: 'relative',
                                    left: -12,
                                    top: -12,
                                    color: theme.palette.success.main,
                                    backgroundColor: 'black',
                                    borderRadius: 12,
                                  }}
                                />
                              </Box>
                            )}
                            <Typography textAlign="center" sx={{ p: 1 }}>
                              {lp.name}
                            </Typography>
                            <Divider
                              sx={{
                                borderColor: theme.palette.primary.dark,
                                '&:hover': {
                                  borderColor: 'inherit',
                                },
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pt: 1,
                                pb: 2,
                                gap: 1,
                              }}
                            >
                              <Typography>
                                {lp.courseIDs.length} Courses
                              </Typography>
                              <img
                                src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${lp.imageName}`}
                                style={{
                                  height: 160,
                                  width: 160,
                                  flexGrow: 0,
                                }}
                                alt={lp.name}
                              />
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  </Box>
                ),
              )}
            </React.Fragment>
          )
        },
      )}
      {!selectedCategories.length && (
        <Typography sx={{ mt: 2 }}>
          Please select one or more categories to view learning paths.
        </Typography>
      )}
      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected('')}
        PaperProps={{
          sx: {
            mt: 8,
            backgroundImage: 'none',
            bgcolor: 'background.default',
            p: 1,
            borderLeft: `2px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box
          sx={{
            mb: 0.5,
            px: 0.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ flex: '0 0 fit-content' }}>
            <Avatar
              src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${selectedPath?.imageName}`}
            />
          </Box>
          <Box sx={{ flex: '1 1 fit-content' }}>{selectedPath?.name}</Box>
          <Box sx={{ flex: '0 0 fit-content' }}>
            <IconButton
              onClick={() => setSelected('')}
              sx={{
                '&:hover': { bgcolor: theme.palette.background.paper },
              }}
            >
              <CancelIcon />
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            columnGap: 1,
            mb: 1,
            px: 0.5,
          }}
        >
          <LinearProgress
            sx={{ flex: '1 1 50px' }}
            variant="determinate"
            value={pathCompletion}
          />
          <Typography variant="body2">
            {(Math.round(pathCompletion * 100) / 100).toFixed(0)}% Complete
          </Typography>
          <Tooltip title="Reset Progress">
            <IconButton
              color="error"
              size="small"
              sx={{
                ml: -1,
                '&:hover': { bgcolor: theme.palette.background.paper },
              }}
              onClick={() => {
                setDialog('reset')
              }}
            >
              <ResetIcon />
            </IconButton>
          </Tooltip>
          <ConfirmAction
            executeAction={() =>
              axios
                .put(
                  `/api/profile/learning-path/reset?learningPathID=${selected}`,
                )
                .then(() => dispatch(fetchLearningPaths()))
                .then(() => dispatch(fetchLearningPathCourses(selected)))
                .then(() =>
                  setSuccessMessage('Learning Path successfully reset!'),
                )
            }
            open={dialog}
            setOpen={setDialog}
            openText="reset"
            title="Confirm Progress Reset"
            text="Are you sure you want to reset progress for this learning path?"
            onFailure={(err) =>
              setErrorMessage(err.message || 'Error Resetting Learning Path')
            }
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'scroll',
            mb: 8,
          }}
        >
          {selectedPath?.courseIDs.map((courseID) => {
            let c = courses[selected].find((course) => course.id === courseID)
            return !c ? (
              <></>
            ) : (
              <Box
                key={c.id}
                sx={{
                  p: 0.5,
                  border: `1px solid ${theme.palette.primary.dark}`,
                  borderRadius: 20,
                  bgcolor: 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    cursor: 'pointer',
                    bgcolor: theme.palette.background.paper,
                  },
                }}
                onClick={() => navigate(`/self-paced/course/${courseID}`)}
              >
                <Box sx={{ flex: '0 0 fit-content' }}>
                  <Avatar
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${c.imageName}`}
                  />
                </Box>
                <Box sx={{ flex: '0 1 fit-content' }}>{c.name}</Box>
                <Box sx={{ flex: '1 0 1px' }}></Box>
                {c.status === 'Complete' ? (
                  <Chip
                    sx={{ width: 112, mr: '5px', color: 'black' }}
                    color="success"
                    variant="filled"
                    label="Completed"
                    icon={<CorrectIcon />}
                  />
                ) : (
                  <Chip
                    sx={{ width: 112, mr: '5px', bgcolor: 'black' }}
                    color="error"
                    variant="outlined"
                    label="Incomplete"
                    icon={<IncorrectIcon />}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </Drawer>
      <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setSuccessMessage('')}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[learningPathsStatus, linkedServicesStatus, userStatus]}
      />
    </>
  )
}
